/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import component types */
import type { KpiCarouselApi2LastValueItem } from '@/components/api/KpiCarouselApi2LastValue';
import type { KpiCarouselApi2TimeSeriesItem } from '@/components/api/KpiCarouselApi2TimeSeries';
import type { KpiCarouselStaticItem } from '@/components/kpi/KpiCarouselStatic';

/* Import libraries */
import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import type { TimeSeriesBandMarker, TimeSeriesPlotType } from '@/lib/timeSeries/timeSeriesPlotly';
import type { TimeSeriesCalc, TimeSeriesTrafficLightConfig, TimeSeriesValueLabelsConfig } from '@/lib/timeSeries/timeSeriesValue';

/* Import types */
import type { StoredPlotlyResponse } from '@/types/eeCheck';

export const EXAMPLE_STATIC_PARSED_TIME_SERIES: ParsedTimeSeriesCsv = {
    columns: ['local server time', 'Measured [kWh]', 'Predicted [kWh]'],
    plotXAxisColumn: 'local server time',
    plotXAxisValues: [
        '2026-01-01 00:00:00',
        '2026-01-02 00:00:00',
        '2026-01-03 00:00:00',
        '2026-01-04 00:00:00',
        '2026-01-05 00:00:00',
        '2026-01-06 00:00:00',
        '2026-01-07 00:00:00',
        '2026-01-08 00:00:00',
        '2026-01-09 00:00:00',
        '2026-01-10 00:00:00',
        '2026-01-11 00:00:00',
        '2026-01-12 00:00:00',
    ],
    plotYAxisColumns: ['Measured [kWh]', 'Predicted [kWh]'],
    plotYAxisValues: {
        'Measured [kWh]': [6.5, 7.1, 6.9, 7.4, 7.5, 8.1, 7.9, 8.4, 6.5, 7.1, 6.9, 7.4],
        'Predicted [kWh]': [6.2, 6.8, 7.0, 7.1, 7.2, 8.8, 8.0, 8.1, 7.2, 8.8, 8.0, 8.1],
    },
};

export const EXAMPLE_STATIC_STORED_PLOTLY_FIGURES: Array<{
    codeName: string;
    figure: StoredPlotlyResponse;
    label: string;
}> = [
    {
        codeName: 'basicBarChartFigure',
        label: 'Basic Bar Chart',
        figure: {
            data: [
                {
                    x: ['Building A', 'Building B', 'Building C', 'Building D'],
                    y: [12, 18, 15, 20],
                    type: 'bar',
                    name: 'Base load',
                    marker: {
                        color: '#0d6efd',
                    },
                },
            ],
            layout: {
                title: {
                    text: 'Base load by building',
                },
                xaxis: {
                    title: {
                        text: 'Building',
                    },
                },
                yaxis: {
                    title: {
                        text: 'Load in kW',
                    },
                },
            },
            config: {
                responsive: true,
                displaylogo: false,
            },
        },
    },
    {
        codeName: 'stackedBarChartFigure',
        label: 'Stacked Bar Chart',
        figure: {
            data: [
                {
                    x: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
                    y: [42, 45, 44, 47, 49],
                    type: 'bar',
                    name: 'Base load',
                    marker: {
                        color: '#0d6efd',
                    },
                },
                {
                    x: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
                    y: [18, 21, 20, 19, 22],
                    type: 'bar',
                    name: 'Heating energy',
                    marker: {
                        color: '#fd7e14',
                    },
                },
            ],
            layout: {
                title: {
                    text: 'Daily energy demand split',
                },
                barmode: 'stack',
                xaxis: {
                    title: {
                        text: 'Weekday',
                    },
                },
                yaxis: {
                    title: {
                        text: 'Energy in MWh',
                    },
                },
            },
            config: {
                responsive: true,
                displaylogo: false,
            },
        },
    },
    {
        codeName: 'groupedBarChartFigure',
        label: 'Grouped Bar Chart',
        figure: {
            data: [
                {
                    x: ['Plant 1', 'Plant 2', 'Plant 3'],
                    y: [110, 96, 124],
                    type: 'bar',
                    name: 'Measured base load',
                    marker: {
                        color: '#198754',
                    },
                },
                {
                    x: ['Plant 1', 'Plant 2', 'Plant 3'],
                    y: [104, 102, 118],
                    type: 'bar',
                    name: 'Predicted base load',
                    marker: {
                        color: '#6f42c1',
                    },
                },
            ],
            layout: {
                title: {
                    text: 'Measured vs predicted base load',
                },
                barmode: 'group',
                xaxis: {
                    title: {
                        text: 'Plant',
                    },
                },
                yaxis: {
                    title: {
                        text: 'Load in kW',
                    },
                },
            },
            config: {
                responsive: true,
                displaylogo: false,
            },
        },
    },
    {
        codeName: 'lineChartFigure',
        label: 'Line Chart',
        figure: {
            data: [
                {
                    x: [
                        '2026-01-01 00:00:00',
                        '2026-01-02 00:00:00',
                        '2026-01-03 00:00:00',
                        '2026-01-04 00:00:00',
                        '2026-01-05 00:00:00',
                    ],
                    y: [62, 64, 61, 66, 68],
                    type: 'scatter',
                    mode: 'lines',
                    name: 'Measured heating load',
                    line: {
                        color: '#dc3545',
                        width: 3,
                    },
                },
                {
                    x: [
                        '2026-01-01 00:00:00',
                        '2026-01-02 00:00:00',
                        '2026-01-03 00:00:00',
                        '2026-01-04 00:00:00',
                        '2026-01-05 00:00:00',
                    ],
                    y: [59, 61, 60, 63, 65],
                    type: 'scatter',
                    mode: 'lines',
                    name: 'Predicted heating load',
                    line: {
                        color: '#0dcaf0',
                        width: 3,
                    },
                },
            ],
            layout: {
                title: {
                    text: 'Heating load trend',
                },
                xaxis: {
                    title: {
                        text: 'Period',
                    },
                },
                yaxis: {
                    title: {
                        text: 'Load in kW',
                    },
                },
            },
            config: {
                responsive: true,
                displaylogo: false,
            },
        },
    },
    {
        codeName: 'basicPieChartFigure',
        label: 'Basic Pie Chart',
        figure: {
            data: [
                {
                    labels: ['Base load', 'Heating', 'Ventilation', 'PV self-use'],
                    values: [42, 28, 18, 12],
                    type: 'pie',
                    textinfo: 'label+percent',
                },
            ],
            layout: {
                title: {
                    text: 'Energy share overview',
                },
            },
            config: {
                responsive: true,
                displaylogo: false,
            },
        },
    },
    {
        codeName: 'basicSankeyDiagramFigure',
        label: 'Basic Sankey Diagram',
        figure: {
            data: [
                {
                    type: 'sankey',
                    arrangement: 'fixed',
                    node: {
                        pad: 18,
                        thickness: 18,
                        line: {
                            color: 'rgba(15, 23, 42, 0.18)',
                            width: 1,
                        },
                        x: [0.02, 0.02, 0.02, 0.42, 0.82, 0.82, 0.82, 0.82, 0.82],
                        y: [0.15, 0.45, 0.75, 0.45, 0.08, 0.28, 0.48, 0.68, 0.88],
                        label: [
                            'Grid',
                            'PV',
                            'BHPP',
                            'Total supply (2.000 MWh)',
                            'Building/Electricity Consumption',
                            'Heat Pump',
                            'Battery',
                            'Grid Feed-in',
                            'Losses',
                        ],
                        color: [
                            '#2563eb',
                            '#f59e0b',
                            '#10b981',
                            '#334155',
                            '#64748b',
                            '#ef4444',
                            '#8b5cf6',
                            '#0ea5e9',
                            '#94a3b8',
                        ],
                    },
                    link: {
                        source: [0, 1, 2, 3, 3, 3, 3, 3, 3],
                        target: [3, 3, 3, 4, 5, 6, 7, 8],
                        value: [1000, 600, 400, 900, 500, 300, 200, 100],
                        color: [
                            'rgba(37, 99, 235, 0.45)',
                            'rgba(245, 158, 11, 0.45)',
                            'rgba(16, 185, 129, 0.45)',
                            'rgba(100, 116, 139, 0.45)',
                            'rgba(239, 68, 68, 0.45)',
                            'rgba(139, 92, 246, 0.45)',
                            'rgba(14, 165, 233, 0.45)',
                            'rgba(148, 163, 184, 0.45)',
                        ],
                    },
                },
            ],
            layout: {
                title: {
                    text: 'Annual consumption in 2025',
                },
                font: {
                    size: 12,
                },
            },
            config: {
                responsive: true,
                displaylogo: false,
            },
        },
    },
];

export const EXAMPLE_STATIC_TIME_SERIES_PLOT_TYPES: Array<{
    label: string;
    value: TimeSeriesPlotType;
}> = [
    {
        label: 'Line Chart',
        value: 'line',
    },
    {
        label: 'Stacked Bar Chart',
        value: 'stackedBar',
    },
    {
        label: 'Grouped Bar Chart',
        value: 'groupedBar',
    },
];

export interface ApiTimeSeriesPlotExample {
    code: string;
    label: string;
    props: {
        bandMarkers?: TimeSeriesBandMarker[];
        right?: string[];
        urn: string[];
    };
}

export const API_TIME_SERIES_PLOT_EXAMPLES: ApiTimeSeriesPlotExample[] = [
    {
        label: 'Band markers',
        code: `<Api2TimeSeriesPlot
    from="NOW-3MONTHS"
    to="NOW"
    urn={[
        'GN.Z01.P_15m',
        'calc.AD.N.Z01.P.PV.pred_recent',
        'calc.AD.N.Z01.P.PV.error',
    ]}
    bandMarkers={[
        {
            series: 'calc.AD.N.Z01.P.PV.error',
            label: 'Error',
            color: 'rgba(239, 68, 68, 0.16)',
        },
    ]}
    title="PV prediction GN.Z01.P"
    xAxisTitle="Period"
    yAxisTitle="Power in kWh"
/>`,
        props: {
            urn: [
                'GN.Z01.P_15m',
                'calc.AD.N.Z01.P.PV.pred_recent',
                'calc.AD.N.Z01.P.PV.error',
            ],
            bandMarkers: [
                {
                    series: 'calc.AD.N.Z01.P.PV.error',
                    label: 'Error',
                    color: 'rgba(239, 68, 68, 0.16)',
                },
            ],
        },
    },
    {
        label: 'Right axis series',
        code: `<Api2TimeSeriesPlot
    from="NOW-3MONTHS"
    to="NOW"
    urn={[
        'GN.Z01.P_15m',
        'calc.AD.N.Z01.P.PV.pred_recent',
        'calc.AD.N.Z01.P.PV.error',
    ]}
    right={['calc.AD.N.Z01.P.PV.error']}
    title="PV prediction GN.Z01.P"
    xAxisTitle="Period"
    yAxisTitle="Power in kWh"
/>`,
        props: {
            urn: [
                'GN.Z01.P_15m',
                'calc.AD.N.Z01.P.PV.pred_recent',
                'calc.AD.N.Z01.P.PV.error',
            ],
            right: ['calc.AD.N.Z01.P.PV.error'],
        },
    },
    {
        label: 'Two series only',
        code: `<Api2TimeSeriesPlot
    from="NOW-3MONTHS"
    to="NOW"
    urn={[
        'GN.Z01.P_15m',
        'calc.AD.N.Z01.P.PV.pred_recent',
    ]}
    title="PV prediction GN.Z01.P"
    xAxisTitle="Period"
    yAxisTitle="Power in kWh"
/>`,
        props: {
            urn: [
                'GN.Z01.P_15m',
                'calc.AD.N.Z01.P.PV.pred_recent',
            ],
        },
    },
];

export interface KpiValueStaticExample {
    code: string;
    label: string;
    props: {
        conversion?: number;
        date?: string | null;
        fractionDigits?: number;
        info?: string;
        link?: string;
        modal?: {
            buttonLabel?: string;
            message: string;
            title?: string;
        };
        offset?: number;
        showLegend?: boolean;
        title?: string;
        trafficLight?: TimeSeriesTrafficLightConfig;
        unit?: string | null;
        value: number | null;
        valueLabels?: TimeSeriesValueLabelsConfig;
    };
}

export const KPI_VALUE_STATIC_EXAMPLES: KpiValueStaticExample[] = [
    {
        label: 'Default energy value',
        code: `<KpiValueStatic
    value={42.5}
    title="Static KPI - Energy"
    unit="MWh"
    conversion={0.001}
/>`,
        props: {
            value: 42.5,
            title: 'Static KPI - Energy',
            unit: 'MWh',
            conversion: 0.001,
        },
    },
    {
        label: 'Default percent value',
        code: `<KpiValueStatic
    value={0.97}
    title="Static KPI - Percent"
    unit="%"
    conversion={100}
/>`,
        props: {
            value: 0.97,
            title: 'Static KPI - Percent',
            unit: '%',
            conversion: 100,
        },
    },
    {
        label: 'Default currency value',
        code: `<KpiValueStatic
    value={123}
    title="Static KPI - Currency"
    unit="€"
    fractionDigits={2}
    conversion={1}
/>`,
        props: {
            value: 123,
            title: 'Static KPI - Currency',
            unit: '€',
            fractionDigits: 2,
            conversion: 1,
        },
    },
    {
        label: 'Success traffic light',
        code: `<KpiValueStatic
    value={0.97}
    title="Static KPI - Success"
    unit="%"
    conversion={100}
    trafficLight={{
        rules: [
            { when: 'lt', value: 0.6, severity: 'danger', title: 'Static KPI - Danger' },
            { when: 'lt', value: 0.9, severity: 'warning', title: 'Static KPI - Warning' },
        ],
        fallback: 'success',
    }}
    showLegend={true}
    info="This KPI compares the current static value against the configured traffic light thresholds."
/>`,
        props: {
            value: 0.97,
            title: 'Static KPI - Success',
            unit: '%',
            conversion: 100,
            info: 'This KPI compares the current static value against the configured traffic light thresholds.',
            trafficLight: {
                rules: [
                    { when: 'lt', value: 0.6, severity: 'danger', title: 'Static KPI - Danger' },
                    { when: 'lt', value: 0.9, severity: 'warning', title: 'Static KPI - Warning' },
                ],
                fallback: 'success',
            },
            showLegend: true
        },
    },
    {
        label: 'Warning traffic light',
        code: `<KpiValueStatic
    value={0.75}
    title="Static KPI - Success"
    unit="%"
    conversion={100}
    trafficLight={{
        rules: [
            { when: 'lt', value: 0.6, severity: 'danger', title: 'Static KPI - Danger' },
            { when: 'lt', value: 0.9, severity: 'warning', title: 'Static KPI - Warning' },
        ],
        fallback: 'success',
    }}
    showLegend={true}
    info="This KPI compares the current static value against the configured traffic light thresholds."
/>`,
        props: {
            value: 0.75,
            title: 'Static KPI - Success',
            unit: '%',
            conversion: 100,
            info: 'This KPI compares the current static value against the configured traffic light thresholds.',
            trafficLight: {
                rules: [
                    { when: 'lt', value: 0.6, severity: 'danger', title: 'Static KPI - Danger' },
                    { when: 'lt', value: 0.9, severity: 'warning', title: 'Static KPI - Warning' },
                ],
                fallback: 'success',
            },
            showLegend: true
        },
    },
    {
        label: 'Danger traffic light',
        code: `<KpiValueStatic
    value={0.42}
    title="Static KPI - Success"
    unit="%"
    conversion={100}
    trafficLight={{
        rules: [
            { when: 'lt', value: 0.6, severity: 'danger', title: 'Static KPI - Danger' },
            { when: 'lt', value: 0.9, severity: 'warning', title: 'Static KPI - Warning' },
        ],
        fallback: 'success',
    }}
    showLegend={true}
    info="This KPI compares the current static value against the configured traffic light thresholds."
/>`,
        props: {
            value: 0.42,
            title: 'Static KPI - Success',
            unit: '%',
            conversion: 100,
            info: 'This KPI compares the current static value against the configured traffic light thresholds.',
            trafficLight: {
                rules: [
                    { when: 'lt', value: 0.6, severity: 'danger', title: 'Static KPI - Danger' },
                    { when: 'lt', value: 0.9, severity: 'warning', title: 'Static KPI - Warning' },
                ],
                fallback: 'success',
            },
            showLegend: true
        },
    },
    {
        label: 'Link interaction',
        code: `<KpiValueStatic
    value={42.5}
    title="Static KPI - Link"
    unit="MWh"
    conversion={0.001}
    link="/#/anomalies/pv-prediction"
/>`,
        props: {
            value: 42.5,
            title: 'Static KPI - Link',
            unit: 'MWh',
            conversion: 0.001,
            link: '/#/anomalies/pv-prediction',
        },
    },
    {
        label: 'Modal interaction',
        code: `<KpiValueStatic
    value={0.97}
    title="Static KPI - Modal"
    unit="%"
    conversion={100}
    modal={{
        title: "Static KPI details",
        message: "This is a simple example modal for a KPI tile.",
        buttonLabel: "OK",
    }}
/>`,
        props: {
            value: 0.97,
            title: 'Static KPI - Modal',
            unit: '%',
            conversion: 100,
            modal: {
                title: 'Static KPI details',
                message: 'This is a simple example modal for a KPI tile.',
                buttonLabel: 'OK',
            },
        },
    },
    {
        label: 'Manual date',
        code: `<KpiValueStatic
    value={42.5}
    title="Static KPI - Date"
    unit="MWh"
    conversion={0.001}
    date="2026-07-03 12:00:00"
/>`,
        props: {
            value: 42.5,
            title: 'Static KPI - Date',
            unit: 'MWh',
            conversion: 0.001,
            date: '2026-07-03 12:00:00',
        },
    },
];

export interface KpiValueApi2TimeSeriesExample {
    code: string;
    label: string;
    props: {
        aggregation: 'last';
        calc?: TimeSeriesCalc;
        calcCode?: string;
        conversion: number;
        fractionDigits?: number;
        from: string;
        info?: string;
        series?: string;
        showDate?: boolean;
        showLegend?: boolean;
        title: string;
        to: string;
        trafficLight?: TimeSeriesTrafficLightConfig;
        unit: string;
        urn: string | string[];
        valueLabels?: TimeSeriesValueLabelsConfig;
    };
}

export interface KpiValueApi2LastValueExample {
    code: string;
    label: string;
    props: {
        calc?: TimeSeriesCalc;
        calcCode?: string;
        conversion: number;
        date: string;
        fractionDigits?: number;
        info?: string;
        series?: string;
        showDate?: boolean;
        showLegend?: boolean;
        title: string;
        trafficLight?: TimeSeriesTrafficLightConfig;
        unit: string;
        urn: string | string[];
        valueLabels?: TimeSeriesValueLabelsConfig;
    };
}

export interface KpiCarouselStaticExample {
    code: string;
    label: string;
    props: {
        autoAdvanceMs?: number;
        info?: string;
        items: KpiCarouselStaticItem[];
        showLegend?: boolean;
    };
}

export const KPI_VALUE_API2_TIME_SERIES_EXAMPLES: KpiValueApi2TimeSeriesExample[] = [
    {
        label: 'Energy value',
        code: `<KpiValueApi2TimeSeries
    from="NOW-4WEEKS"
    to="NOW"
    urn="calc.AD.BA2.Z34.P.PV.recent_30"
    aggregation="last"
    title="Predicted PV value"
    conversion={1 / 1000000}
    unit="MWh"
/>`,
        props: {
            from: 'NOW-4WEEKS',
            to: 'NOW',
            urn: 'calc.AD.BA2.Z34.P.PV.recent_30',
            aggregation: 'last',
            title: 'Predicted PV value',
            conversion: 1 / 1000000,
            unit: 'MWh',
        },
    },
    {
        label: 'Percent value',
        code: `<KpiValueApi2TimeSeries
    from="NOW-4WEEKS" // See: https://ee-check.easd/monitor/test/date_placeholders
    to="NOW"
    urn="calc.AD.BA2.Z34.P.PV.ratio_30"
    aggregation="last"
    title="Ratio of Measurement to Prediction"
    conversion={100}
    unit="%"
/>`,
        props: {
            from: 'NOW-4WEEKS',
            to: 'NOW',
            urn: 'calc.AD.BA2.Z34.P.PV.ratio_30',
            aggregation: 'last',
            title: 'Ratio of Measurement to Prediction',
            conversion: 100,
            unit: '%',
        },
    },
    {
        label: 'Binary yes/no value',
        code: `<KpiValueApi2TimeSeries
    from="NOW-4WEEKS" // See: https://ee-check.easd/monitor/test/date_placeholders
    to="NOW"
    urn="calc.AD.N.Z01.P.PV.error"
    series="calc.AD.N.Z01.P.PV.error"
    aggregation="last"
    title="Error"
    valueLabels={{
        values: {
            '0': 'No',
            '1': 'Yes',
        },
        fallback: '-',
    }}
    trafficLight={{
        rules: [
            { when: 'gt', value: 0, severity: 'danger' },
        ],
        fallback: 'success',
    }}
    showDate={true}
    showLegend={true}
/>`,
        props: {
            from: 'NOW-4WEEKS',
            to: 'NOW',
            urn: 'calc.AD.N.Z01.P.PV.error',
            series: 'calc.AD.N.Z01.P.PV.error',
            aggregation: 'last',
            title: 'Error',
            conversion: 1,
            showDate: true,
            trafficLight: {
                rules: [
                    { when: 'gt', value: 0, severity: 'danger' },
                ],
                fallback: 'success',
            },
            unit: '',
            valueLabels: {
                values: {
                    '0': 'No',
                    '1': 'Yes',
                },
                fallback: '-',
            },
            showLegend: true,
        },
    },
    {
        label: 'Calculated status value',
        code: `<KpiValueApi2TimeSeries
    from="NOW-4WEEKS" // See: https://ee-check.easd/monitor/test/date_placeholders
    to="NOW"
    urn="calc.AD.N.Z01.P.PV.dtc"
    calc={({ values }) => ({
        status: (values.dtc ?? 0) >= 200 ? 2 : (values.dtc ?? 0) >= 100 ? 1 : 0,
    })}
    calcCode={"({ values }) => ({\n        status: (values.dtc ?? 0) >= 200 ? 2 : (values.dtc ?? 0) >= 100 ? 1 : 0,\n    })"}
    series="status"
    aggregation="last"
    title="Error"
    valueLabels={{
        values: {
            '0': 'OK',
            '1': 'Abweichende Leistung',
            '2': 'Genereller Fehler',
            '3': 'Genereller Fehler',
        },
        fallback: '-',
    }}
    trafficLight={{
        rules: [
            { when: 'gte', value: 2, severity: 'danger' },
            { when: 'gte', value: 1, severity: 'warning' },
        ],
        fallback: 'success',
    }}
    showDate={true}
    showLegend={true}
/>`,
        props: {
            from: 'NOW-4WEEKS',
            to: 'NOW',
            urn: 'calc.AD.N.Z01.P.PV.dtc',
            calc: ({ values }) => ({
                status: (values.dtc ?? 0) >= 200 ? 2 : (values.dtc ?? 0) >= 100 ? 1 : 0,
            }),
            calcCode: `({ values }) => ({
    status: (values.dtc ?? 0) >= 200 ? 2 : (values.dtc ?? 0) >= 100 ? 1 : 0,
})`,
            series: 'status',
            aggregation: 'last',
            title: 'Error',
            conversion: 1,
            showDate: true,
            trafficLight: {
                rules: [
                    { when: 'gte', value: 2, severity: 'danger' },
                    { when: 'gte', value: 1, severity: 'warning' },
                ],
                fallback: 'success',
            },
            unit: '',
            valueLabels: {
                values: {
                    '0': 'OK',
                    '1': 'Abweichende Leistung',
                    '2': 'Genereller Fehler',
                },
                fallback: '-',
            },
            showLegend: true,
        },
    },
];

export const KPI_CAROUSEL_STATIC_EXAMPLES: KpiCarouselStaticExample[] = [
    {
        label: 'Static KPI carousel',
        code: `<KpiCarouselStatic
    autoAdvanceMs={8000}
    items={[
        {
            key: 'ratio',
            value: 97,
            title: 'Static KPI - Ratio',
            unit: '%',
            date: '2026-07-03 12:00:00',
            trafficLight: {
                rules: [
                    { when: 'lt', value: 60, severity: 'danger' },
                    { when: 'lt', value: 90, severity: 'warning' },
                ],
                fallback: 'success',
            },
        },
        {
            key: 'measured',
            value: 62.5,
            title: 'Static KPI - Measured',
            unit: 'kWh',
            date: '2026-07-03 12:00:00',
            trafficLight: {
                rules: [
                    { when: 'lt', value: 60, severity: 'danger' },
                    { when: 'lt', value: 90, severity: 'warning' },
                ],
                fallback: 'success',
            },
        },
        {
            key: 'predicted',
            value: 38.2,
            title: 'Static KPI - Predicted',
            unit: 'kWh',
            date: '2026-07-03 12:00:00',
            trafficLight: {
                rules: [
                    { when: 'lt', value: 60, severity: 'danger' },
                    { when: 'lt', value: 90, severity: 'warning' },
                ],
                fallback: 'success',
            },
        },
    ]}
/>`,
        props: {
            autoAdvanceMs: 8000,
            items: [
                {
                    key: 'ratio',
                    value: 97,
                    title: 'Static KPI - Ratio',
                    unit: '%',
                    date: '2026-07-03 12:00:00',
                    trafficLight: {
                        rules: [
                            { when: 'lt', value: 60, severity: 'danger' },
                            { when: 'lt', value: 90, severity: 'warning' },
                        ],
                        fallback: 'success',
                    },
                },
                {
                    key: 'measured',
                    value: 62.5,
                    title: 'Static KPI - Measured',
                    unit: 'kWh',
                    date: '2026-07-03 12:00:00',
                    trafficLight: {
                        rules: [
                            { when: 'lt', value: 60, severity: 'danger' },
                            { when: 'lt', value: 90, severity: 'warning' },
                        ],
                        fallback: 'success',
                    },
                },
                {
                    key: 'predicted',
                    value: 38.2,
                    title: 'Static KPI - Predicted',
                    unit: 'kWh',
                    date: '2026-07-03 12:00:00',
                    trafficLight: {
                        rules: [
                            { when: 'lt', value: 60, severity: 'danger' },
                            { when: 'lt', value: 90, severity: 'warning' },
                        ],
                        fallback: 'success',
                    },
                },
            ],
        },
    },
];

export interface KpiCarouselApi2TimeSeriesExample {
    code: string;
    label: string;
    props: {
        autoAdvanceMs?: number;
        from: string;
        info?: string;
        items: KpiCarouselApi2TimeSeriesItem[];
        showDate: boolean;
        showLegend?: boolean;
        to: string;
    };
}

export const KPI_CAROUSEL_API2_TIME_SERIES_EXAMPLES: KpiCarouselApi2TimeSeriesExample[] = [
    {
        label: 'PV KPI carousel',
        code: `<KpiCarouselApi2TimeSeries
    from="NOW-4WEEKS"
    to="NOW"
    autoAdvanceMs={8000}
    showDate={true}
    items={[
        {
            key: "ratio",
            urn: "calc.AD.N.Z01.P.PV.ratio_30",
            series: "calc.AD.N.Z01.P.PV.ratio_30",
            aggregation: "last",
            title: "Ratio of Measurement to Prediction",
            trafficLight: {
                rules: [
                    { when: "lt", value: 0.6, severity: "danger" },
                    { when: "lt", value: 0.9, severity: "warning" },
                ],
                fallback: "success",
            },
        },
        {
            key: "measured",
            urn: "calc.AD.N.Z01.P.PV.ref_30",
            series: "calc.AD.N.Z01.P.PV.ref_30",
            aggregation: "last",
            title: "Measured PV value GN.Z01.P",
            conversion: 1 / 1000000,
            unit: "MWh",
        },
        {
            key: "predicted",
            urn: "calc.AD.N.Z01.P.PV.recent_30",
            series: "calc.AD.N.Z01.P.PV.recent_30",
            aggregation: "last",
            title: "Predicted PV value GN.Z01.P",
            conversion: 1 / 1000000,
            unit: "MWh",
        },
    ]}
/>`,
        props: {
            from: 'NOW-4WEEKS',
            to: 'NOW',
            autoAdvanceMs: 8000,
            showDate: true,
            items: [
                {
                    key: 'ratio',
                    urn: 'calc.AD.N.Z01.P.PV.ratio_30',
                    series: 'calc.AD.N.Z01.P.PV.ratio_30',
                    aggregation: 'last',
                    title: 'Ratio of Measurement to Prediction',
                    trafficLight: {
                        rules: [
                            { when: 'lt', value: 0.6, severity: 'danger' },
                            { when: 'lt', value: 0.9, severity: 'warning' },
                        ],
                        fallback: 'success',
                    },
                },
                {
                    key: 'measured',
                    urn: 'calc.AD.N.Z01.P.PV.ref_30',
                    series: 'calc.AD.N.Z01.P.PV.ref_30',
                    aggregation: 'last',
                    title: 'Measured PV value GN.Z01.P',
                    conversion: 1 / 1000000,
                    unit: 'MWh',
                },
                {
                    key: 'predicted',
                    urn: 'calc.AD.N.Z01.P.PV.recent_30',
                    series: 'calc.AD.N.Z01.P.PV.recent_30',
                    aggregation: 'last',
                    title: 'Predicted PV value GN.Z01.P',
                    conversion: 1 / 1000000,
                    unit: 'MWh',
                },
            ],
        },
    },
    {
        label: 'Single KPI item',
        code: `<KpiCarouselApi2TimeSeries
    from="NOW-4WEEKS"
    to="NOW"
    autoAdvanceMs={8000}
    showDate={true}
    items={[
        {
            key: "ratio",
            urn: "calc.AD.N.Z01.P.PV.ratio_30",
            series: "calc.AD.N.Z01.P.PV.ratio_30",
            aggregation: "last",
            title: "Ratio of Measurement to Prediction",
            trafficLight: {
                rules: [
                    { when: "lt", value: 0.6, severity: "danger" },
                    { when: "lt", value: 0.9, severity: "warning" },
                ],
                fallback: "success",
            },
        },
    ]}
/>`,
        props: {
            from: 'NOW-4WEEKS',
            to: 'NOW',
            autoAdvanceMs: 8000,
            showDate: true,
            items: [
                {
                    key: 'ratio',
                    urn: 'calc.AD.N.Z01.P.PV.ratio_30',
                    series: 'calc.AD.N.Z01.P.PV.ratio_30',
                    aggregation: 'last',
                    title: 'Ratio of Measurement to Prediction',
                    trafficLight: {
                        rules: [
                            { when: 'lt', value: 0.6, severity: 'danger' },
                            { when: 'lt', value: 0.9, severity: 'warning' },
                        ],
                        fallback: 'success',
                    },
                },
            ],
        },
    },
    {
        label: 'Two identical KPI items',
        code: `<KpiCarouselApi2TimeSeries
    from="NOW-4WEEKS"
    to="NOW"
    autoAdvanceMs={8000}
    showDate={true}
    items={[
        {
            key: "ratio-1",
            urn: "calc.AD.N.Z01.P.PV.ratio_30",
            series: "calc.AD.N.Z01.P.PV.ratio_30",
            aggregation: "last",
            title: "Ratio of Measurement to Prediction",
            trafficLight: {
                rules: [
                    { when: "lt", value: 0.6, severity: "danger" },
                    { when: "lt", value: 0.9, severity: "warning" },
                ],
                fallback: "success",
            },
        },
        {
            key: "ratio-2",
            urn: "calc.AD.N.Z01.P.PV.ratio_30",
            series: "calc.AD.N.Z01.P.PV.ratio_30",
            aggregation: "last",
            title: "Ratio of Measurement to Prediction",
            trafficLight: {
                rules: [
                    { when: "lt", value: 0.6, severity: "danger" },
                    { when: "lt", value: 0.9, severity: "warning" },
                ],
                fallback: "success",
            },
        },
    ]}
/>`,
        props: {
            from: 'NOW-4WEEKS',
            to: 'NOW',
            autoAdvanceMs: 8000,
            showDate: true,
            items: [
                {
                    key: 'ratio-1',
                    urn: 'calc.AD.N.Z01.P.PV.ratio_30',
                    series: 'calc.AD.N.Z01.P.PV.ratio_30',
                    aggregation: 'last',
                    title: 'Ratio of Measurement to Prediction',
                    trafficLight: {
                        rules: [
                            { when: 'lt', value: 0.6, severity: 'danger' },
                            { when: 'lt', value: 0.9, severity: 'warning' },
                        ],
                        fallback: 'success',
                    },
                },
                {
                    key: 'ratio-2',
                    urn: 'calc.AD.N.Z01.P.PV.ratio_30',
                    series: 'calc.AD.N.Z01.P.PV.ratio_30',
                    aggregation: 'last',
                    title: 'Ratio of Measurement to Prediction',
                    trafficLight: {
                        rules: [
                            { when: 'lt', value: 0.6, severity: 'danger' },
                            { when: 'lt', value: 0.9, severity: 'warning' },
                        ],
                        fallback: 'success',
                    },
                },
            ],
        },
    },
];

function createLastValueCode(code: string, sourceComponent: string, targetComponent: string): string {
    return code
        .replace(sourceComponent, targetComponent)
        .replace(/    from=.*\n    to=.*\n/, '    date="NOW"\n')
        .replace(/    aggregation="last"\n/g, '')
        .replace(/            aggregation: "last",\n/g, '');
}

export const KPI_VALUE_API2_LAST_VALUE_EXAMPLES: KpiValueApi2LastValueExample[] = [
    ...KPI_VALUE_API2_TIME_SERIES_EXAMPLES.map<KpiValueApi2LastValueExample>((example) => {
        const {
            aggregation: _aggregation,
            from: _from,
            to: _to,
            ...props
        } = example.props;

        return {
            label: example.label,
            code: createLastValueCode(
                example.code,
                'KpiValueApi2TimeSeries',
                'KpiValueApi2LastValue',
            ),
            props: {
                ...props,
                date: 'NOW',
            },
        };
    }),
    {
        label: 'Custom traffic light labels',
        code: `<KpiValueApi2LastValue
    date="NOW"
    urn="PUB.GRID.price_MWh"
    title="Day-Ahead Price"
    conversion={1}
    fractionDigits={2}
    unit="€/MWh"
    trafficLight={{
        labels: {
            danger: 'Expensive',
            success: 'Favourable',
            warning: 'Normal',
        },
        rules: [
            { when: 'gt', value: 120, severity: 'danger' },
            { when: 'gte', value: 50, severity: 'warning' },
        ],
        fallback: 'success',
    }}
    showLegend={true}
/>`,
        props: {
            conversion: 1,
            date: 'NOW',
            fractionDigits: 2,
            title: 'Day-Ahead Price',
            trafficLight: {
                labels: {
                    danger: 'Expensive',
                    success: 'Favourable',
                    warning: 'Normal',
                },
                rules: [
                    { when: 'gt', value: 120, severity: 'danger' },
                    { when: 'gte', value: 50, severity: 'warning' },
                ],
                fallback: 'success',
            },
            unit: '€/MWh',
            urn: 'PUB.GRID.price_MWh',
        },
    },
];

export const KPI_CAROUSEL_API2_LAST_VALUE_EXAMPLES = KPI_CAROUSEL_API2_TIME_SERIES_EXAMPLES.map((example) => {
    const {
        from: _from,
        to: _to,
        ...props
    } = example.props;

    return {
        label: example.label,
        code: createLastValueCode(
            example.code,
            'KpiCarouselApi2TimeSeries',
            'KpiCarouselApi2LastValue',
        ),
        props: {
            ...props,
            date: 'NOW',
            items: props.items.map(({
                aggregation: _aggregation,
                urn,
                ...item
            }): KpiCarouselApi2LastValueItem => ({
                ...item,
                urn: Array.isArray(urn) ? urn[0] ?? '' : urn,
            })),
        },
    };
});

export interface ApiStoredPlotlyPlotExample {
    code: string;
    label: string;
    props: {
        range: string;
        reference?: 'base_load' | 'heating_energy' | 'thermal_conflict';
        title?: string;
        urn: string;
    };
}

export const API_STORED_PLOTLY_PLOT_EXAMPLES: ApiStoredPlotlyPlotExample[] = [
    {
        label: 'Base Load - Raw Plot (14d)',
        code: `<Api2StoredPlotlyPlot
    urn="GN.Z18"
    range="14d"
/>`,
        props: {
            urn: 'GN.Z18',
            range: '14d',
        },
    },
    {
        label: 'Base Load - Titled Plot (14d)',
        code: `<Api2StoredPlotlyPlot
    urn="GN.Z18"
    range="14d"
    title="BaseLoad GN.Z18.P (14d)"
/>`,
        props: {
            urn: 'GN.Z18',
            range: '14d',
            title: 'BaseLoad GN.Z18.P (14d)',
        },
    },
    {
        label: 'Base Load - Raw Plot (all)',
        code: `<Api2StoredPlotlyPlot
    urn="GN.Z18"
    range="all"
/>`,
        props: {
            urn: 'GN.Z18',
            range: 'all',
        },
    },
    {
        label: 'Base Load - Titled Plot (all)',
        code: `<Api2StoredPlotlyPlot
    urn="GN.Z18"
    range="all"
    title="BaseLoad GN.Z18.P (all)"
/>`,
        props: {
            urn: 'GN.Z18',
            range: 'all',
            title: 'BaseLoad GN.Z18.P (all)',
        },
    },
    {
        label: 'Heating Energy - Raw Plot (all)',
        code: `<Api2StoredPlotlyPlot
    urn="GN.Z13"
    reference="heating_energy"
    range="all"
/>`,
        props: {
            urn: 'GN.Z13',
            reference: 'heating_energy',
            range: 'all',
        },
    },
];

export interface StaticMermaidDiagramExample {
    codeName: string;
    definition: string;
    label: string;
}

export const STATIC_MERMAID_DIAGRAM_EXAMPLES: StaticMermaidDiagramExample[] = [
    {
        codeName: 'flowchartMermaidDefinition',
        label: 'Flowchart',
        definition: `flowchart LR
    Grid[Grid]
    PV[PV System]
    Battery[Battery]
    Building[Building Load]

    Grid --> Building
    PV --> Building
    PV --> Battery
    Battery --> Building`,
    },
    {
        codeName: 'sequenceDiagramMermaidDefinition',
        label: 'Sequence Diagram',
        definition: `sequenceDiagram
    participant Portal as Customer Portal
    participant API as EE Check API
    participant Meter as Meter Endpoint

    Portal->>API: Request time series
    API->>Meter: Fetch measurement data
    Meter-->>API: Return current values
    API-->>Portal: Render dashboard response`,
    },
    {
        codeName: 'classDiagramMermaidDefinition',
        label: 'Class Diagram',
        definition: `classDiagram
    class SessionContext {
        +accessToken: string
    }

    class Api2TimeSeriesPlot {
        +from: string
        +to: string
        +urn: string[]
    }

    class StaticStoredPlotlyPlot {
        +figure: StoredPlotlyResponse
    }

    SessionContext <.. Api2TimeSeriesPlot : uses
    StaticStoredPlotlyPlot --> StoredPlotlyResponse : renders`,
    },
];

export interface StaticMarkdownExample {
    codeName: string;
    content: string;
    label: string;
}

export function createMultilineStringConstant(name: string, value: string): string {
    return `const ${name} = \`\n${value}\n\`;`;
}

export const STATIC_MARKDOWN_EXAMPLES: StaticMarkdownExample[] = [
    {
        codeName: 'markdownIntroExample',
        label: 'Heading and paragraphs',
        content: `# EnObserve Overview

This markdown example renders a simple heading and two explanatory paragraphs inside the shared dashboard surface.

## Why it matters

Markdown works well for contextual explanations, KPI descriptions, and short operational notes that should stay easy to maintain.

It can also be mixed with diagrams and charts to document what a dashboard section is showing.`,
    },
    {
        codeName: 'markdownTableExample',
        label: 'Description and table',
        content: `# Base Load Summary

The following table shows an example comparison of current base-load related indicators for several monitored assets.

| Asset | Current load | 14d average | Status |
| --- | ---: | ---: | --- |
| GN.Z18 | 124 kW | 118 kW | Stable |
| GN.Z23 | 141 kW | 136 kW | Increased |
| GN.Z33 | 98 kW | 101 kW | Slightly lower |`,
    },
];
